import {
  ChevronRight,
  FileSearch,
  Clock,
  ExternalLink,
  KeyRound,
  LogOut,
  ShieldCheck,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { caminhoInternoSeguro, destinoDoUsuario } from '@/nucleo/auth/destino';
import { useAuth } from '@/nucleo/auth/useAuth';
import { chamarApi } from '@/nucleo/auth/cliente-api';
import { CAMINHO_DO_ACOMPANHAMENTO } from '@/modulos/portal/credenciamento/AcompanhamentoContrato';
import { type EnvelopeLogin, type EstadoTelaLogin } from '@/nucleo/auth/tipos';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { BotaoGovBr } from '@/ui/BotaoGovBr';
import { Spinner } from '@/ui/Spinner';

import { LayoutAutenticacao } from './LayoutAutenticacao';

/**
 * Frase por motivo de volta a esta tela. O motivo viaja na URL (`?motivo=`), e
 * nao em armazenamento: recarregar nao perde a explicacao, e nada fica guardado
 * no navegador depois.
 */
const AVISO_POR_MOTIVO: Record<string, { titulo: string; texto: string }> = {
  inatividade: {
    titulo: 'Sua sessão foi encerrada por inatividade',
    texto: 'Por segurança, entre novamente para continuar. Nada do que você fez foi perdido.',
  },
  saida: {
    titulo: 'Você saiu do sistema',
    texto: 'Sua sessão foi encerrada nos dois lados: no sistema e na identificação.',
  },
  'sessao-invalida': {
    titulo: 'Sua sessão não é mais válida',
    texto: 'Isso acontece depois de muito tempo aberta ou de uma troca de senha. Entre novamente.',
  },
};

/**
 * /login — a porta de entrada.
 *
 * Aqui NAO se digita senha, e isso e decisao de projeto, nao economia de tela:
 * quem guarda senha e segundo fator e o Keycloak
 * (docs/AUTENTICACAO-E-ACESSO.md, secao 1). O cliente `teg-web` tem a concessao
 * direta por senha DESLIGADA no realm — se esta tela tivesse campo de senha, ela
 * precisaria receber a senha do usuario, e passaria a ser mais um lugar por onde
 * senha circula.
 *
 * Esta tela tambem e o ponto de VOLTA do fluxo de codigo de autorizacao: o
 * provedor de autenticacao reconhece o codigo na URL e conclui a entrada. Por
 * isso ela mostra "concluindo" enquanto o estado e `entrando` — e por isso nao
 * existe uma rota tecnica separada de retorno, que seria rota sem tela.
 *
 * O limite de tentativas e do Keycloak: 5 senhas erradas em 30 minutos bloqueiam
 * a conta temporariamente (criterio E3.3), e a mensagem aparece na propria
 * pagina dele, em portugues.
 */
export function LoginPage() {
  const { estado, usuario, aviso, entrar, entrarGovbr } = useAuth();
  const [parametros] = useSearchParams();
  const [entrandoPeloGovBr, setEntrandoPeloGovBr] = useState(false);
  const [estadoProjetado, setEstadoProjetado] = useState<EstadoTelaLogin | null>(null);

  useEffect(() => {
    void chamarApi<EnvelopeLogin>('/bff/v1/login/state', { publica: true })
      .then((envelope) => setEstadoProjetado(envelope.state))
      .catch(() => setEstadoProjetado(null));
  }, []);

  const voltar = caminhoInternoSeguro(parametros.get('voltar'));
  const motivo = parametros.get('motivo');
  const explicacao = motivo ? AVISO_POR_MOTIVO[motivo] : undefined;

  /**
   * Vai direto ao provedor gov.br, sem passar pela escolha de provedor do
   * Keycloak: `iniciarAutorizacao` e a mesma funcao que `entrar` chama
   * (ProvedorAuth.tsx), so que com a dica de provedor. `carregando` cobre o
   * instante entre o clique e o redirecionamento de topo — que o navegador
   * pode levar um instante para iniciar.
   */
  const aoEntrarPeloGovBr = () => {
    setEntrandoPeloGovBr(true);
    entrarGovbr?.(voltar);
  };

  /**
   * Volta do Keycloak (`?code=` ou `?error=`): quem leva ao destino e o
   * ProvedorAuth, que guardou o caminho antes de sair (deposito-pedido.ts) e
   * navega ao concluir. Esta tela nao redireciona por conta propria nesse
   * instante. No react-router 7 a troca de endereco vai em transicao e o
   * estado `autenticado` chega antes dela: esta tela ainda esta montada, sem
   * `?voltar=`, e o redirecionamento daqui mandava todo mundo para a pagina
   * inicial do perfil, por cima do destino — recarregar uma tela interna
   * terminava no painel.
   */
  const voltaDoKeycloak = parametros.has('code') || parametros.has('error');

  // Ja autenticado: nao faz sentido ficar na entrada.
  if (estado === 'autenticado' && usuario && !voltaDoKeycloak) {
    return <Navigate to={voltar || destinoDoUsuario(usuario)} replace />;
  }

  if (
    estado === 'iniciando' ||
    estado === 'entrando' ||
    (estado === 'autenticado' && voltaDoKeycloak)
  ) {
    return (
      <LayoutAutenticacao
        titulo="Concluindo sua entrada"
        descricao="Estamos confirmando sua identidade. Isso leva alguns segundos."
      >
        <div role="status" aria-live="polite">
          <Spinner size="lg" />
          <p className="text-center text-sm text-muted-foreground">Aguarde um instante…</p>
        </div>
      </LayoutAutenticacao>
    );
  }

  return (
    <LayoutAutenticacao
      titulo={
        estadoProjetado?.fase === 'AGUARDANDO_PROVIDENCIA'
          ? 'Acesso temporariamente indisponível'
          : 'Bem-vindo ao Sistema TEG'
      }
      descricao="Entre com a sua conta para acessar o sistema."
    >
      {explicacao && (
        <Alert className="border-warning/40 bg-warning/10">
          <Clock className="h-4 w-4 text-warning" aria-hidden="true" />
          <AlertTitle>{explicacao.titulo}</AlertTitle>
          <AlertDescription className="text-muted-foreground">{explicacao.texto}</AlertDescription>
        </Alert>
      )}

      {aviso && (
        <Alert variant="destructive">
          <LogOut className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Não foi possível concluir a entrada</AlertTitle>
          <AlertDescription>{aviso}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <Button
          type="button"
          size="lg"
          className="h-12 w-full gap-2 text-sm font-semibold"
          onClick={() => entrar(voltar)}
        >
          Entrar no sistema
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>

        <div className="rounded-xl border border-border bg-muted/40 p-4">
          <p className="flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <span>
              Você será levado à página de identificação do sistema, onde informa e-mail e senha e
              confirma o <strong className="font-semibold text-foreground">segundo fator</strong>.
              No primeiro acesso, você escolhe entre o aplicativo autenticador e um código enviado
              por e-mail.
            </span>
          </p>
        </div>

        <div className="relative py-1" aria-hidden="true">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-background px-2 text-xs uppercase tracking-wider text-muted-foreground">
              ou
            </span>
          </div>
        </div>

        <BotaoGovBr
          carregando={entrandoPeloGovBr}
          disabled={
            estadoProjetado?.provedores.find((provedor) => provedor.id === 'govbr')?.habilitado ===
            false
          }
          onClick={aoEntrarPeloGovBr}
        />

        <p className="text-center text-xs text-muted-foreground">
          {estadoProjetado?.provedores.find((provedor) => provedor.id === 'govbr')
            ?.indisponibilidade ??
            'Entre com sua conta gov.br para identificação sem cadastrar senha neste sistema.'}
        </p>
      </div>

      {/*
       * Quem se inscreveu no credenciamento e ainda não foi aprovado não entra
       * por aqui: o acesso ao sistema só é liberado depois da aprovação. A caixa
       * é a do protótipo (`Auth.tsx`, "Inscrito ainda em análise") e leva ao
       * acompanhamento, que pede a sessão da própria inscrição.
       */}
      <div className="space-y-2 rounded-xl border border-border bg-muted/40 p-3">
        <p className="text-xs leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Sua inscrição ainda está em análise?</strong> O acesso
          ao portal só é liberado após a aprovação. Enquanto isso, acompanhe o andamento por aqui:
        </p>
        <Button asChild variant="outline" size="sm" className="h-10 w-full gap-1.5 rounded-lg">
          <Link to={CAMINHO_DO_ACOMPANHAMENTO}>
            <FileSearch className="h-4 w-4" aria-hidden="true" />
            Acompanhar minha inscrição
          </Link>
        </Button>
      </div>

      <div className="space-y-3 border-t border-border pt-4 text-center text-sm">
        <p>
          <Link
            to="/esqueci-minha-senha"
            className="inline-flex items-center gap-1.5 font-medium text-link underline-offset-4 hover:underline"
          >
            <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
            Esqueci minha senha
          </Link>
        </p>
        <p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            Ir para o portal público
          </Link>
        </p>
      </div>
    </LayoutAutenticacao>
  );
}

export default LoginPage;
